"use client";

// FILE: src/features/nmck-calculator/components/cost-breakdown-bar.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Визуализация структуры цены НМЦК — stacked bar chart, легенда, мета-карточки и детальная разбивка по статьям затрат (Приказ Росгвардии №45).
// SCOPE: Только отображение. Не выполняет расчёт — принимает готовый CalculateNmtckResponse.
// INPUT: CalculateNmtckResponse (fot_total, sv_total, direct_cost_total, indirect_cost_total, profit_total, vat_amount, posts_count, total_hours, base_hour_rate, nmtck_total).
// OUTPUT: React-разметка (stacked bar + legend 2×3 + meta grid 3 карточки + detail breakdown 6 строк).
// KEYWORDS: DOMAIN(NMCC): CostBreakdown; DOMAIN(Visualization): StackedBarChart; TECH(React): PureComponent; MATH(ProportionalSegments)
// END_MODULE_CONTRACT
//
// START_RATIONALE:
// Q: Почему сегменты бар-чарта не совпадают с дизайн-прототипом (Материалы 8% / Оборудование / Прочее 5%)?
// A: Прототип использует упрощённую модель расчёта (МРОТ × разряд × коэфф.). Реальный калькулятор работает по методике Приказа Росгвардии №45: ФОТ → Страховые взносы → Прямые затраты (×U) → Косвенные → Прибыль → НДС. Сегменты отражают РЕАЛЬНУЮ структуру данных, а не выдуманные категории, которых в CalculateNmtckResponse не существует.
// Q: Почему «Усложнение (U)» выделено отдельным сегментом?
// A: direct_cost_total = (fot_total + sv_total) × U. Если показать fot + sv + direct_cost, получится double-counting. Сегмент «Усложнение» = direct_cost_total − fot_total − sv_total — это надбавка за коэффициент сложности (вооружение, массовые мероприятия, антитеррор и т.д.). Сумма 6 сегментов = svohr_total + vat_amount ≈ nmtck_total.
// Q: Почему проценты считаются от суммы сегментов, а не от nmtck_total?
// A: При ненулевых СТСО/страховании/инфляции nmtck_total ≠ svohr_total + vat. Базируя проценты на сумме сегментов, bar всегда заполняется на 100% без визуальных разрывов.
// END_RATIONALE
//
// START_CHANGE_SUMMARY:
// LAST_CHANGE: 1.0.0 — Создан компонент. Stacked bar (6 сегментов), legend (2×3), meta grid (3 карточки), detail breakdown (6 строк с мини-барами).
// END_CHANGE_SUMMARY
//
// START_MODULE_MAP:
// COMP [10] Компонент визуализации структуры цены => CostBreakdownBar
// FUNC [3] Форматирование числа в ₽ => formatRub
// FUNC [4] Расчёт массива сегментов с долями => buildSegments
// CONST [5] Палитра цветов статей затрат => SEGMENT_DEFS
// END_MODULE_MAP

import { CalculateNmtckResponse } from "../actions/calculate";

interface CostBreakdownBarProps {
  /** Результат расчёта НМЦК от RPC calculate_nmtck */
  result: CalculateNmtckResponse;
}

/**
 * Определения сегментов структуры цены.
 * Каждый сегмент: ключ, подпись, hex-цвет, функция извлечения значения из result.
 * Порядок определяет очерёдность слева направо в stacked bar.
 *
 * Сумма всех getValue(result) = fot + sv + (direct − fot − sv) + indirect + profit + vat
 *                              = direct_cost_total + indirect_cost_total + profit_total + vat_amount
 *                              = svohr_total + vat_amount
 */
const SEGMENT_DEFS = [
  {
    key: "fot",
    label: "ФОТ",
    color: "#22c55e",
    getValue: (r: CalculateNmtckResponse) => r.fot_total,
  },
  {
    key: "sv",
    label: "Взносы 30,2%",
    color: "#10b981",
    getValue: (r: CalculateNmtckResponse) => r.sv_total,
  },
  {
    key: "u_complexity",
    label: "Усложнение (U)",
    color: "#34d399",
    getValue: (r: CalculateNmtckResponse) =>
      Math.max(0, r.direct_cost_total - r.fot_total - r.sv_total),
  },
  {
    key: "indirect",
    label: "Косвенные расходы",
    color: "#eab308",
    getValue: (r: CalculateNmtckResponse) => r.indirect_cost_total,
  },
  {
    key: "profit",
    label: "Прибыль",
    color: "#16a34a",
    getValue: (r: CalculateNmtckResponse) => r.profit_total,
  },
  {
    key: "vat",
    label: "НДС",
    color: "#a3a3a3",
    getValue: (r: CalculateNmtckResponse) => r.vat_amount,
  },
] as const;

/**
 * Форматирует число в строку с разделением разрядов и символом ₽.
 * Округление до целого — для визуализации структуры цены достаточная точность.
 */
function formatRub(n: number): string {
  return Math.round(n).toLocaleString("ru-RU") + " ₽";
}

/**
 * Строит массив сегментов с вычисленными значениями и процентными долями.
 * Процент считается от суммы всех сегментов (не от nmtck_total),
 * чтобы bar всегда заполнялся на 100%.
 */
function buildSegments(result: CalculateNmtckResponse) {
  const rows = SEGMENT_DEFS.map((def) => ({
    key: def.key,
    label: def.label,
    color: def.color,
    value: def.getValue(result),
  }));
  const total = rows.reduce((sum, r) => sum + r.value, 0);
  const safeTotal = total > 0 ? total : 1;
  return {
    rows: rows.map((r) => ({
      ...r,
      pct: (r.value / safeTotal) * 100,
    })),
    total,
  };
}

/**
 * CostBreakdownBar — визуализация структуры цены НМЦК.
 *
 * Состоит из 4 блоков:
 * 1. Stacked bar — горизонтальная полоса из 6 цветных сегментов
 * 2. Legend — сетка 2×3 с цветными квадратиками и суммами в ₽
 * 3. Meta grid — 3 карточки (Постов, Цена за час, Базовая ставка)
 * 4. Detail breakdown — 6 строк с названием статьи, процентом и мини-баром
 */
export function CostBreakdownBar({ result }: CostBreakdownBarProps) {
  const { rows, total } = buildSegments(result);

  // START_BLOCK_META: Мета-карточки — ключевые показатели расчёта
  const perHour =
    result.total_hours > 0
      ? result.nmtck_total / result.total_hours
      : 0;
  const metaCards = [
    {
      label: "Постов",
      value: String(result.posts_count),
      sub: `${result.total_hours.toLocaleString("ru-RU")} ч всего`,
    },
    {
      label: "Цена за час",
      value: Math.round(perHour).toLocaleString("ru-RU"),
      sub: "₽/час",
    },
    {
      label: "Базовая ставка",
      value: result.base_hour_rate.toFixed(2),
      sub: "₽/час",
    },
  ];
  // END_BLOCK_META

  return (
    <div className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] p-5 space-y-5">
      {/* === 1. Stacked bar === */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--portal-text-muted))] mb-3 font-mono">
          Структура цены — наведите для доли
        </div>
        <div
          className="flex h-9 border border-[hsl(var(--portal-border-hover))] overflow-hidden bg-[hsl(var(--portal-bg))]"
          role="img"
          aria-label="Структура цены контракта"
        >
          {rows.map((seg) => (
            <div
              key={seg.key}
              style={{
                backgroundColor: seg.color,
                width: `${seg.pct}%`,
              }}
              className="h-full transition-all duration-500 min-w-0"
              title={`${seg.label}: ${formatRub(seg.value)} (${seg.pct.toFixed(1)}%)`}
            />
          ))}
        </div>
      </div>

      {/* === 2. Legend — сетка 2×3 === */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
        {rows.map((seg) => (
          <div
            key={seg.key}
            className="flex items-center gap-2.5 text-[13px] text-[hsl(var(--portal-text-secondary))]"
          >
            <span
              className="w-3 h-3 flex-none border border-[hsl(var(--portal-border-hover))]"
              style={{ backgroundColor: seg.color }}
            />
            <span>{seg.label}</span>
            <span className="ml-auto font-mono font-bold text-[13px] text-[hsl(var(--portal-text))]">
              {formatRub(seg.value)}
            </span>
          </div>
        ))}
      </div>

      {/* === 3. Meta grid — 3 карточки === */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[hsl(var(--portal-surface-2))] border border-[hsl(var(--portal-border-hover))]">
        {metaCards.map((card) => (
          <div key={card.label} className="bg-[hsl(var(--portal-bg))] p-4">
            <span className="block text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--portal-text-muted))] mb-2 font-mono">
              {card.label}
            </span>
            <b className="block text-[22px] leading-none font-mono text-[hsl(var(--portal-text))]">
              {card.value}
            </b>
            <small className="block text-[11px] uppercase tracking-[0.04em] text-[hsl(var(--portal-text-muted))] mt-1.5 font-mono">
              {card.sub}
            </small>
          </div>
        ))}
      </div>

      {/* === 4. Detail breakdown — 6 строк с мини-барами === */}
      <div className="pt-4 border-t border-[hsl(var(--portal-border))]">
        <div className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--portal-text-muted))] mb-3.5 font-mono">
          Детальная разбивка
        </div>
        <div className="space-y-3">
          {rows.map((seg) => (
            <div key={seg.key}>
              <div className="flex justify-between items-center text-[13px] mb-1.5">
                <span className="flex items-center gap-2 text-[hsl(var(--portal-text))]">
                  <span
                    className="w-2.5 h-2.5 border border-[hsl(var(--portal-border-hover))]"
                    style={{ backgroundColor: seg.color }}
                  />
                  {seg.label}
                </span>
                <span className="font-mono text-[hsl(var(--portal-text-secondary))]">
                  {formatRub(seg.value)} · {seg.pct.toFixed(1)}%
                </span>
              </div>
              <div className="h-1.5 bg-[hsl(var(--portal-surface-2))] relative overflow-hidden">
                <div
                  className="absolute left-0 top-0 bottom-0 transition-all duration-500"
                  style={{
                    backgroundColor: seg.color,
                    width: `${seg.pct}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Итоговая сумма для контекста */}
      <div className="text-[10px] uppercase tracking-[0.04em] text-[hsl(var(--portal-text-muted))] font-mono pt-2 border-t border-[hsl(var(--portal-border))]">
        Сумма статей: {formatRub(total)} · НМЦК итого:{" "}
        {formatRub(result.nmtck_total)}
      </div>
    </div>
  );
}
